import { OfficeAccountingTableHeader } from "./officeAccountingConsts";

export type OfficeAccountingColumnConfig = {
  title: string;
  id: string;
  width?: number;
};

/** Legacy uniform width used before header-based defaults. */
const defaultWidth = 5;

/**
 * Estimate rem width from header title length (padding + optional edit icon).
 * Caps keep very long titles from dominating the table.
 */
const estimateOAPColumnWidthFromTitle = (title: string): number => {
  const estimated = title.length * 0.52 + 2.25;
  return Math.round(Math.max(defaultWidth, Math.min(estimated, 16)) * 100) / 100;
};

/** Floors for columns that usually need more space than the header alone. */
const OAP_CONTENT_MIN_WIDTH: Partial<Record<string, number>> = {
  fullName: 11.5,
  email: 9.69,
  managersEmail: 10,
  agentsEmail: 9.5,
  course: 9.69,
  fieldOfStudy: 10,
  comments: 8,
  phone: 7.5,
  dateOfBirth: 7.5,
};

const resolveOAPColumnWidth = (id: string, title: string): number => {
  const fromTitle = estimateOAPColumnWidthFromTitle(title);
  const contentMin = OAP_CONTENT_MIN_WIDTH[id] ?? 0;
  return Math.max(fromTitle, contentMin);
};

export const officeAccountingAllFieldsList: OfficeAccountingColumnConfig[] = OfficeAccountingTableHeader.map((column) => ({
  title: column.title,
  id: column.id,
  width: resolveOAPColumnWidth(column.id, column.title),
}));

/** Default visible columns through Current Semester + Status. */
const defaultVisibleColumnIds = [
  "officeAccountingId",
  "applicantId",
  "fullName",
  "comments",
  "campus",
  "country",
  "dateOfBirth",
  "passportNo",
  "passportIssued",
  "validPassport",
  "phone",
  "email",
  "managersEmail",
  "agentsEmail",
  "levelEducation",
  "course",
  "fieldOfStudy",
  "formOfEducation",
  "duration",
  "mediumOfInstruction",
  "admissionSemester",
  "intake",
  "currentSemester",
  "generalStatus",
];

export const officeAccountingDefaultConfiguration: OfficeAccountingColumnConfig[] = defaultVisibleColumnIds
  .map((id) => officeAccountingAllFieldsList.find((col) => col.id === id))
  .filter((col): col is OfficeAccountingColumnConfig => col != null);

const findOAPColumn = (id: string): OfficeAccountingColumnConfig | undefined =>
  officeAccountingAllFieldsList.find((col) => col.id === id);

const resolveSavedOAPColumnWidth = (
  savedWidth: number | undefined,
  canonicalWidth: number | undefined,
): number => {
  const canonical = canonicalWidth ?? defaultWidth;
  // Upgrade old uniform `5` defaults to header-based widths; keep user-resized values.
  if (savedWidth == null || savedWidth === defaultWidth) return canonical;
  return savedWidth;
};

const insertOAPColumnAfter = (
  columns: OfficeAccountingColumnConfig[],
  column: OfficeAccountingColumnConfig,
  afterId: string,
): OfficeAccountingColumnConfig[] => {
  const index = columns.findIndex((col) => col.id === afterId);
  if (index === -1) return [column, ...columns];
  return [...columns.slice(0, index + 1), column, ...columns.slice(index + 1)];
};

const dedupeOAPColumns = (columns: OfficeAccountingColumnConfig[]): OfficeAccountingColumnConfig[] => {
  const seen = new Set<string>();
  return columns.filter((col) => {
    if (seen.has(col.id)) return false;
    seen.add(col.id);
    return true;
  });
};

const insertOAPColumnBefore = (
  columns: OfficeAccountingColumnConfig[],
  column: OfficeAccountingColumnConfig,
  beforeId: string,
): OfficeAccountingColumnConfig[] => {
  const index = columns.findIndex((col) => col.id === beforeId);
  if (index === -1) return [column, ...columns];
  return [...columns.slice(0, index), column, ...columns.slice(index)];
};

/** Migrates saved OAPTable: legacy `id` ("ID No") → officeAccountingId + applicantId. */
export function normalizeVisibleOAPColumns(columns: OfficeAccountingColumnConfig[]): OfficeAccountingColumnConfig[] {
  const knownIds = new Set(officeAccountingAllFieldsList.map((col) => col.id));
  const hadLegacyIdColumn = columns.some((col) => col.id === "id");

  let next: OfficeAccountingColumnConfig[] = columns.flatMap((col) => {
    if (col.id === "id") {
      const officeAccountingIdCol = findOAPColumn("officeAccountingId");
      const applicantIdCol = findOAPColumn("applicantId");
      const width = resolveSavedOAPColumnWidth(col.width, officeAccountingIdCol?.width);
      return [officeAccountingIdCol, applicantIdCol]
        .filter((item): item is OfficeAccountingColumnConfig => item != null)
        .map((item) => ({
          ...item,
          width: item.id === "officeAccountingId" ? width : resolveSavedOAPColumnWidth(col.width, item.width),
        }));
    }

    const canonical = findOAPColumn(col.id);
    if (!canonical) return [];
    return [{ ...canonical, width: resolveSavedOAPColumnWidth(col.width, canonical.width) }];
  });

  next = dedupeOAPColumns(next.filter((col) => knownIds.has(col.id)));

  // Forward migration: keep Certification & Equalization after Tuition Fee when Tuition is visible.
  if (next.some((col) => col.id === "tuitionFee") && !next.some((col) => col.id === "certificationEqualization")) {
    const certificationCol = findOAPColumn("certificationEqualization");
    if (certificationCol) next = insertOAPColumnAfter(next, { ...certificationCol }, "tuitionFee");
  }

  if (!hadLegacyIdColumn) return next;

  if (!next.some((col) => col.id === "officeAccountingId")) {
    const officeAccountingIdCol = findOAPColumn("officeAccountingId");
    if (officeAccountingIdCol) next = [officeAccountingIdCol, ...next];
  }

  if (!next.some((col) => col.id === "applicantId")) {
    const applicantIdCol = findOAPColumn("applicantId");
    if (applicantIdCol) {
      const anchorId = next.some((col) => col.id === "officeAccountingId") ? "officeAccountingId" : "fullName";
      next = insertOAPColumnAfter(next, applicantIdCol, anchorId);
    }
  }

  const courseIndex = next.findIndex((col) => col.id === "course");
  let degreeIndex = next.findIndex((col) => col.id === "levelEducation");
  if (courseIndex !== -1 && degreeIndex === -1) {
    const degreeColumn = findOAPColumn("levelEducation");
    if (degreeColumn) {
      next = insertOAPColumnBefore(next, { ...degreeColumn }, "course");
      degreeIndex = next.findIndex((col) => col.id === "levelEducation");
    }
  }

  const updatedCourseIndex = next.findIndex((col) => col.id === "course");
  const updatedDegreeIndex = next.findIndex((col) => col.id === "levelEducation");
  if (updatedCourseIndex !== -1 && updatedDegreeIndex !== -1 && updatedDegreeIndex > updatedCourseIndex) {
    const [degreeColumn] = next.splice(updatedDegreeIndex, 1);
    const courseIdxAfterRemoval = next.findIndex((col) => col.id === "course");
    next.splice(courseIdxAfterRemoval, 0, degreeColumn);
  }

  return next;
}

export const OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED = "Non Sorted";

export const officeAccountingGeneralStatusConst = [
  { id: "active", label: "Active" },
  { id: "on_leave", label: "On leave" },
  { id: "graduated", label: "Graduated" },
  { id: "non_sorted", label: OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED },
] as const;

/** Status Admission Payment options (Office Accounting pop-up). */
export const officeAccountingAdmissionPaymentStatusConst = [
  { id: "waiting", label: "Waiting" },
  { id: "debt", label: "Debt" },
  { id: "paid", label: "Paid" },
] as const;

export const resolveOfficeAccountingAdmissionPaymentStatusLabel = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "—";
  const match = officeAccountingAdmissionPaymentStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.label ?? trimmed;
};

export const resolveOfficeAccountingAdmissionPaymentStatusId = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "empty";
  const match = officeAccountingAdmissionPaymentStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.id ?? "empty";
};

export const resolveOfficeAccountingGeneralStatusLabel = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED;
  const match = officeAccountingGeneralStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.label ?? trimmed;
};

export const resolveOfficeAccountingGeneralStatusId = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "non_sorted";
  const match = officeAccountingGeneralStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.id ?? "non_sorted";
};

export const formatOfficeAccountingGeneralStatusForApi = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED) return "";
  return resolveOfficeAccountingGeneralStatusLabel(trimmed);
};

export const officeAccountingFilterStatusesConst = ["All", "Active", "On leave", "Graduated"];

export const officeAccountingHeaderSelectConst = [
  { id: "ALL", title: "All" },
  { id: "MALTA", title: "Malta" },
  { id: "POLAND", title: "Poland" },
  { id: "UKRAINE", title: "Ukraine" },
];
